import { useState, useRef } from 'react';
import { Canvg } from 'canvg';
import { EnrichmentResult } from '../types';
import { downloadZip, downloadFile } from '@/utils/downloadFile';
import { buildEnrichmentResultsCsv } from '../enrichmentCsv';

export const useEnrichmentDownload = () => {
  const [csvData, setCsvData] = useState<string | null>(null);
  const [metadataContent, setMetadataContent] = useState<string | null>(null);
  const swarmRef = useRef<HTMLDivElement | null>(null);

  const generateCSV = (results: EnrichmentResult[]) => buildEnrichmentResultsCsv(results);

  // Convert SVG → PNG Blob
  const convertSVGToPNG = async (svgElement: SVGElement): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) throw new Error('Could not get canvas context');

    canvas.width = svgElement.getBoundingClientRect().width || 800;
    canvas.height = svgElement.getBoundingClientRect().height || 600;

    const clonedSvg = svgElement.cloneNode(true) as SVGElement;

    // Create white background
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("width", "100%");
    rect.setAttribute("height", "100%");
    rect.setAttribute("fill", "white");

    clonedSvg.insertBefore(rect, clonedSvg.firstChild);

    // SVG with white background
    const svgData = new XMLSerializer().serializeToString(clonedSvg);

    // Render with Canvg
    const canvg = Canvg.fromString(ctx, svgData, {
      ignoreClear: true,
    });

    await canvg.render();

    // Convert canvas → Blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) reject("PNG conversion failed");
        else resolve(blob);
      }, "image/png");
    });
  };

  // ZIP Download (CSV + Metadata)
  const downloadZIP = async (csvData: string, metadata: string, searchType: string) => {
    await downloadZip({
      zipName: `enrichment_${searchType.toLowerCase()}`,
      files: [
        { name: "enrichment_results.csv", content: csvData },
        { name: "enrichment_metadata.txt", content: metadata },
      ],
    });
  };

  // Graph PNG Download
  const downloadGraphPNG = async () => {
    try {
      if (!swarmRef.current) {
        throw new Error('Graph not found');
      }

      const svgElement = swarmRef.current.querySelector('svg');
      if (!svgElement) {
        throw new Error('Graph SVG not found');
      }

      const pngBlob = await convertSVGToPNG(svgElement);
      
      downloadFile({ blob: pngBlob, filename: "enrichment_graph.png" });

    } catch (error) {
      console.error('Error downloading graph:', error);
      throw error;
    }
  };

  return {
    csvData,
    setCsvData,
    metadataContent,
    setMetadataContent,
    swarmRef,
    generateCSV,
    downloadZIP,
    downloadGraphPNG,
  };
};

